import * as React from 'react';
import { FlexBoxColumn, FlexBoxInline, PageThirdHeaderSx, ThirdBtnSx } from '../../@shared/templates';
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Chip, Divider, Tooltip, Typography } from '@mui/material';
import { SchemaItem, StructureColumnTypeEnum } from '../../@shared/Models/Models';
import { DatasetReportItem } from '../../@shared/Models/DatasetReport';
import { Palette, Sizes } from '../../@shared/palette';
import { useDatasetReport } from '../../hooks/useDatasetReport';
import SchemaItemReport from './SchemaItemReport';
import { AlertComponent } from '../../components/AlertComponent';
import { NotificationModel, NotificationModelType } from '../../reducers/main/mainModel';


export type TabsContentSchemaBaseProps = {
  schemaItems: SchemaItem[],
  schemaId: string
}

type TabsContentSchemaProps = TabsContentSchemaBaseProps & {
  isVisible: boolean
}

const TabsContentSchema = (props: TabsContentSchemaProps) => {
  const [copiedKey, setCopiedKey] = React.useState<string | null>(null);
  const { report, isLoading } = useDatasetReport(props.schemaId, props.isVisible);

  const schemaInfo = props.schemaItems.filter(x => x.typeId === StructureColumnTypeEnum.Info);
  const reportBySchemaItem = report?.schemaItemReport ?? {};

  const listProps = { copiedKey, setCopiedKey, reportBySchemaItem };

  return <div hidden={!props.isVisible}>
    <Box sx={{ ...FlexBoxInline, gap: "10px", marginBottom: "10px" }}>
      {!isLoading && !report?.lastUpdated && <AlertComponent notification={{ message: "Dataset report is not calculated yet", type: NotificationModelType.Warning } as NotificationModel} />}
    </Box>
    <Box sx={{ ...FlexBoxInline, alignItems: "flex-start", gap: "15px" }}>
      <SchemaList {...listProps} schema={props.schemaItems.filter(x => x.typeId === StructureColumnTypeEnum.Feature)} title="Feature" />
      <Divider orientation="vertical" flexItem />
      <SchemaList {...listProps} schema={props.schemaItems.filter(x => x.typeId === StructureColumnTypeEnum.Target)} title="Target" />
      {
        schemaInfo.length > 0 &&
        <>
          <Divider orientation="vertical" flexItem />
          <SchemaList {...listProps} schema={schemaInfo} title="Info" />
        </>
      }
    </Box>
  </div>
}

export type TabsContentSchemaList = {
  schema: SchemaItem[],
  title: string,
  copiedKey: string | null,
  setCopiedKey: (value: string | null) => void,
  reportBySchemaItem: Record<string, DatasetReportItem[]>
}

const SchemaList = ({ schema, title, copiedKey, setCopiedKey, reportBySchemaItem }: TabsContentSchemaList) => {
  return <Box sx={{ ...FlexBoxColumn, width: "33%" }}>
    <Typography sx={{ ...PageThirdHeaderSx }}>
      {title}
    </Typography>
    <Box sx={{ ...FlexBoxColumn, width: "100%" }}>
      {schema.map((field) => (
        <Accordion key={field.id} sx={{ width: "100%", backgroundColor: Palette.backgroundColorPrimary }}>
          <AccordionSummary
            aria-controls={`${field.id}-panel1-content`}
            id={`${field.id}-panel1-header`}
          >
            <Box sx={{ ...FlexBoxColumn, width: "100%" }}>
              <Box sx={{
                ...FlexBoxInline,
                justifyContent: "space-between",
                width: "100%",
              }}>
                <Tooltip title={copiedKey === field.name ? "Copied!" : "Copy"} placement="right" arrow>
                  <Button 
                    sx={{
                      ...ThirdBtnSx, 
                      color: Palette.textColorPrimary,
                      fontSize: Sizes.textPrimary,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      minWidth: "0",
                      textAlign: "left",
                    }} 
                    onClick={(e) => {
                      setCopiedKey(field.name);
                      navigator.clipboard.writeText(field.name);
                      e.stopPropagation();
                      e.preventDefault();
                    }}>{field.name}</Button>
                </Tooltip>
                <Typography sx={{ color: Palette.textColorSecondary, fontSize: Sizes.textSecondary }}>
                  {field.valueTypeId.replace(/([a-z])([A-Z])/g, '$1 $2').trim()} {field.arrayLength && "[" + field.arrayLength + "]"}
                </Typography>
              </Box>
              {field.categories && field.categories.length > 0 && <Box sx={{ marginTop: "5px", ...FlexBoxInline, gap: "5px", flexWrap: "wrap" }}>
                {field.categories.map((c, i) => (
                  <Chip
                    key={c.index}
                    label={
                      <>
                        {c.value}
                      </>
                    }
                    size="small"
                    sx={{
                      backgroundColor: Palette.backgroundColorCard,
                      color: Palette.textColorPrimary,
                      fontSize: Sizes.textPrimary,
                      border: Palette.borderPrimary
                    }}
                  />
                ))}
              </Box>
              }
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            {field.id && reportBySchemaItem[field.id] && <SchemaItemReport schemaItem={field} items={reportBySchemaItem[field.id]} />}
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  </Box>

};

export default TabsContentSchema;
